import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField, readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';
import { scoreNamedIntegerFragmentInteger } from './formula-variable-resolution';
import type { FormulaVariableMetadata, FormulaVariableReaders } from './formula-variable-resolution';
import { integerContributionSource, integerFragmentBraces } from './status-integer-contribution';
import type { IntegerContributionSource } from './status-integer-contribution';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { md5PhpJson } from './php-json-serialize';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (row: unknown,key: string): unknown => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row,key)
    ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
const validUuid = (value: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
const trim = (value: string): string => value.replace(/^[ \t\n\r\0\v]+|[ \t\n\r\0\v]+$/g,'');
function detached<T>(value: T): T {
    if (!arrayLike(value)) return value;
    const entries = Object.entries(value);
    return (entries.every(([key],i)=>key===String(i)) ? entries.map(([,child])=>detached(child))
        : Object.fromEntries(entries.map(([key,child])=>[key,detached(child)]))) as T;
}
export interface EquippedIntegerReaders extends FormulaVariableReaders {
    queryFullItem: (id: string) => unknown;
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
    itemName: (id: string) => unknown;
}
export interface EquippedIntegerDependencies extends FormulaReadDependencies {
    metadata: FormulaVariableMetadata;
    queryFullItem: (id: string) => unknown;
}
export interface EquippedIntegerSource extends IntegerContributionSource { raw_value?: number; suppressed?: boolean }

/** Exact display identity, including serialize/MD5 for anonymous retained rows. */
export function equippedInventoryDisplayKey(row: ItemFieldRow,readers: FormulaVariableReaders): string {
    if (!arrayLike(row)) throw new TypeError('Inventory display key requires a PHP array');
    const iid = readInventoryField(row,'inventory_id');
    if (phpJsonTruthy(iid) && validUuid(phpJsonString(iid))) return 'inv:' + phpJsonString(iid);
    const tid = phpJsonString(readers.itemField(row,'item_id') ?? '');
    return validUuid(tid) ? 'item:' + tid : 'row:' + md5PhpJson(row);
}

/** Actual two-lane equipped discovery. The full-nested layer lane deliberately
 * does not repeat encounter inventory's equipped/absorption gate.
 */
export function collectEquippedInventoryRows(row: ItemFieldRow,readers: EquippedIntegerReaders): ItemFieldRow[] {
    return collectRows(row,readers).map(detached);
}
function collectRows(row: ItemFieldRow,readers: EquippedIntegerReaders): ItemFieldRow[] {
    if (!arrayLike(row)) throw new TypeError('Equipped collector requires a PHP array');
    const byKey = new Map<string,ItemFieldRow>();
    const consider = (candidate: ItemFieldRow): void => {
        if (Object.keys(candidate).length === 0) return;
        const key = equippedInventoryDisplayKey(candidate,readers), previous = byKey.get(key);
        if (previous === undefined) { byKey.set(key,candidate); return; }
        const iid = readInventoryField(candidate,'inventory_id'), previousId = readInventoryField(previous,'inventory_id');
        if (phpJsonTruthy(iid) && validUuid(phpJsonString(iid)) && !(phpJsonTruthy(previousId) && validUuid(phpJsonString(previousId)))) byKey.set(key,candidate);
    };
    const inventory = own(row,'inventory');
    if (arrayLike(inventory)) for (const candidate of Object.values(inventory)) {
        if (!arrayLike(candidate) || readItemUuidSlot(readInventoryField(candidate,'equipped') ?? '') === '') continue;
        if (phpJsonInteger(readInventoryField(candidate,'inventory_private') ?? 0) === 2n) continue;
        consider(candidate);
    }
    const equipped = own(row,'inventory_equipped');
    if (arrayLike(equipped)) for (const raw of Object.values(equipped)) {
        if (arrayLike(raw) && (own(raw,'items') != null || own(raw,'item_layer_types') != null)) { consider(raw); continue; }
        let id: unknown;
        if (arrayLike(raw) && Object.prototype.propertyIsEnumerable.call(raw,'item_layer_effect')) {
            // Only item_uuid from resolve_inventory_layer_list_entry is consumed.
            // Its template/quantity conversions have no side effects or reader calls.
            id = readLayerListEntryUuid(raw);
        } else { id = arrayLike(raw) ? own(raw,'0') ?? '' : raw; }
        if (typeof id !== 'string' || id === '') continue;
        id = id.split('|',1)[0]!;
        if (!validUuid(id as string)) continue;
        const template = readers.queryFullItem(id as string);
        if (!arrayLike(template)) continue;
        const candidate = Object.fromEntries(Object.entries(template));
        if (!phpJsonTruthy(readers.itemField(candidate,'item_id'))) candidate.item_id = id;
        consider(candidate);
    }
    return [...byKey.values()];
}

export function equippedEnchantmentFragments(row: ItemFieldRow,layer: string,readers: EquippedIntegerReaders): unknown[] {
    if (!arrayLike(row)) throw new TypeError('Equipped enchantment requires a PHP array');
    if (layer == null || arrayLike(layer)) throw new TypeError('Equipped layer requires a scalar string');
    layer = phpJsonString(layer);
    const passives = own(own(own(own(row,'computed'),'enchantment'),'abilities'),'passive_ability'), fragments: unknown[] = [];
    if (!arrayLike(passives)) return fragments;
    for (const passive of Object.values(passives)) {
        if (!arrayLike(passive) || own(passive,'passive_conditions_active') === false) continue;
        let ability = own(passive,'item_arr') ?? own(passive,'item_id') ?? null;
        if (typeof ability === 'string' && validUuid(ability)) ability = readers.resolveCarrierRef(ability);
        if (arrayLike(ability)) fragments.push(...Object.values(readers.layerEntries(ability,layer)));
    }
    return detached(fragments);
}
export function equippedInventoryLabel(row: ItemFieldRow,readers: EquippedIntegerReaders): {label: string; href_id: string} {
    if (!arrayLike(row)) throw new TypeError('Inventory label requires a PHP array');
    let label = trim(phpJsonString(readInventoryField(row,'inventory_name') ?? ''));
    if (label === '') {
        const id = readers.itemField(row,'item_id') ?? '';
        label = typeof id === 'string' && validUuid(id) ? phpJsonString(readers.itemField(row,'item_name') ?? readers.itemName(id)) : 'Equipped item';
    }
    let href = '';
    if (phpJsonTruthy(readInventoryField(row,'inventory_id')) && validUuid(phpJsonString(readInventoryField(row,'inventory_id')))) href = phpJsonString(readInventoryField(row,'inventory_id'));
    else if (phpJsonTruthy(readers.itemField(row,'item_id')) && validUuid(phpJsonString(readers.itemField(row,'item_id')))) href = phpJsonString(readers.itemField(row,'item_id'));
    return {label,href_id:href};
}
export function compileEquippedIntegerContribution(row: ItemFieldRow,rawLayer: string,metadata: FormulaVariableMetadata,readers: EquippedIntegerReaders): {
    total: number; sources: EquippedIntegerSource[]; locked: boolean;
} {
    const result = equippedIntegerContributionValue(row,rawLayer,metadata,readers);
    return {...result,total:Number(result.total)};
}
export function equippedIntegerContributionValue(row: ItemFieldRow,rawLayer: string,metadata: FormulaVariableMetadata,readers: EquippedIntegerReaders): {
    total: bigint; sources: EquippedIntegerSource[]; locked: boolean;
} {
    if (rawLayer == null || arrayLike(rawLayer)) throw new TypeError('Equipped layer requires a scalar string');
    const layer = phpJsonString(rawLayer), perEquipmentSlot = layer === 'ac_mod_non_magical';
    const rows: {fixed: boolean; slot: string; value: bigint; source: EquippedIntegerSource}[] = [];
    // Scoring only reads these rows. Detach the public collector's results, but
    // do not deep-copy every entire equipped carrier again for each integer layer.
    for (const inventory of collectRows(row,readers)) {
        // layer_entries normalizes to a list; enchantment appends values only.
        const fragments = [...Object.values(readers.layerEntries(inventory,layer)),...equippedEnchantmentFragments(inventory,layer,readers)];
        if (!fragments.length) continue;
        const braces = integerFragmentBraces(fragments), fixed = braces.length > 0;
        const value = scoreNamedIntegerFragmentInteger(row,fragments,layer,metadata,readers);
        if (value === 0n && !fixed) continue;
        const {label,href_id} = equippedInventoryLabel(inventory,readers);
        const slot = perEquipmentSlot ? readItemUuidSlot(readers.layerValue(inventory,'equipment_slot') ?? '') || '__unslotted__' : '__all__';
        rows.push({fixed,slot,value,source:integerContributionSource('inventory_equipped',label,value,href_id,'inventory_equipped',fixed ? braces.reduce((a,b)=>a>b?a:b) : null)});
    }
    if (!rows.length) return {total:0n,sources:[],locked:false};
    const winners = new Map<string,number>();
    for (let i = 0; i < rows.length; i++) {
        const current = rows[i]!;
        const key = current.slot.toUpperCase(), winner = winners.get(key);
        if (winner === undefined || (current.fixed && !rows[winner]!.fixed)
            || (current.fixed === rows[winner]!.fixed && current.value > rows[winner]!.value)) winners.set(key,i);
    }
    const winnerIndexes = new Set(winners.values()); let total = 0n, locked = false;
    for (const winner of winnerIndexes) { total += rows[winner]!.value; locked ||= rows[winner]!.fixed; }
    return {total,locked,sources:rows.map((row,i)=>winnerIndexes.has(i) ? row.source
        : {...row.source,raw_value:Number(row.value),value:0,suppressed:true})};
}
export function createEquippedIntegerCompiler(deps: EquippedIntegerDependencies) {
    const context = createFormulaReadContext(deps);
    const readers: EquippedIntegerReaders = {...context,
        queryFullItem:id=>{deps.trace?.(['full-item',id]); return deps.queryFullItem(id);},
        resolveCarrierRef:id=>{
            deps.trace?.(['reference',id]); const value = deps.resolveCarrierRef(id);
            if (value !== null && !arrayLike(value)) throw new TypeError('Equipped reference requires a PHP array or null');
            return value;
        },
        itemName:id=>{deps.trace?.(['name',id]); if (!deps.resolveItemName) throw new TypeError('Equipped item name requires an explicit lookup'); return deps.resolveItemName(id);},
    };
    return {collect:(row:ItemFieldRow)=>collectEquippedInventoryRows(row,readers),key:(row:ItemFieldRow)=>equippedInventoryDisplayKey(row,readers),
        label:(row:ItemFieldRow)=>equippedInventoryLabel(row,readers),enchantment:(row:ItemFieldRow,layer:string)=>equippedEnchantmentFragments(row,layer,readers),
        contribution:(row:ItemFieldRow,layer:string)=>compileEquippedIntegerContribution(row,layer,deps.metadata,readers)};
}
